import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { SuggestionsProvider } from "@/shared/components/filters/suggestions";
import {
  customFieldDisplayName,
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";
import {
  renderDashOrValue,
  TimestampCell,
  timestampCellClass,
  type ColDef,
  type ColumnPickerOption,
} from "@/shared/foundation/primitives/Table";
import { formatBoolean } from "@/shared/text";

/** Row shape the custom-field column valueGetter reads from. */
export interface RowWithCustomFields {
  custom_fields?: Record<string, unknown>;
}

const CUSTOM_FIELD_ID_PREFIX = "custom.";

/**
 * The single identifier for a custom field across the grid: the AG-Grid colId,
 * the FilterDefinition.field, and (via table sort) the sort key.
 */
function customFieldColumnId(field: CustomFieldRecord): string {
  return `${CUSTOM_FIELD_ID_PREFIX}${field.field_name}`;
}

/** True for a `custom.<name>` column id (as opposed to `metadata.*` or static). */
export function isCustomFieldColumnId(id: string): boolean {
  return id.startsWith(CUSTOM_FIELD_ID_PREFIX);
}

/**
 * Build the faceted-filter definitions for a set of Ready custom fields. Each is
 * marked `nullable`, since a custom field can be left unset on an entity.
 *
 * `suggestionsFor`, when given, supplies each String field's value
 * suggestions (typically `customFieldValueSuggestions`); the other types
 * need none, an enum's values shipping with its definition.
 */
export function buildCustomFieldFilterDefinitions(
  readyFields: CustomFieldRecord[],
  fieldPrefix = "",
  suggestionsFor?: (field: CustomFieldRecord) => SuggestionsProvider,
): FilterDefinition[] {
  return readyFields.map((field) => {
    const base = {
      field: `${fieldPrefix}${customFieldColumnId(field)}`,
      name: customFieldDisplayName(field),
      nullable: true,
      ...(field.description !== null && { description: field.description }),
    };
    switch (field.field_type) {
      case CustomFieldType.String:
        return suggestionsFor === undefined
          ? { ...base, type: "string" }
          : { ...base, type: "string", suggestions: suggestionsFor(field) };
      case CustomFieldType.Number:
        return { ...base, type: "numeric" };
      case CustomFieldType.Timestamp:
        return { ...base, type: "date" };
      case CustomFieldType.Boolean:
        return { ...base, type: "boolean" };
      case CustomFieldType.Enum:
        return {
          ...base,
          type: "enum",
          options: field.options?.enum_values ?? [],
        };
      default: {
        // exhaustiveness guard
        const exhaustive: never = field.field_type;
        throw new Error(`Unhandled custom field type: ${String(exhaustive)}`);
      }
    }
  });
}

/**
 * Build add/removable, sortable grid columns for a set of Ready custom fields.
 * Values live in the search response under `record.custom_fields[name]`
 */
export function buildCustomFieldColumns<
  TData extends RowWithCustomFields = RowWithCustomFields,
>(readyFields: CustomFieldRecord[]): ColDef<TData>[] {
  return readyFields.map((field) => {
    const column: ColDef<TData> = {
      colId: customFieldColumnId(field),
      headerName: customFieldDisplayName(field),
      minWidth: 120,
      // Safe because the colId doubles as the backend sort key (see customFieldColumnId).
      sortable: true,
      valueGetter: (params) =>
        params.data?.custom_fields?.[field.field_name] ?? null,
      cellRenderer: renderDashOrValue,
    };
    if (field.field_type === CustomFieldType.Timestamp) {
      // A cell renderer rather than a valueFormatter: the grid caches a
      // formatted value, while TimestampCell re-renders when the viewer's
      // date/time preference loads or changes. Timestamp values arrive as
      // ISO-8601 strings, or null when unset.
      column.cellRenderer = TimestampCell;
      column.cellClass = timestampCellClass;
    } else if (field.field_type === CustomFieldType.Boolean) {
      column.valueFormatter = (params) =>
        params.value === null ? "" : formatBoolean(Boolean(params.value));
    }
    return column;
  });
}

/** Build column-picker options for a set of Ready custom fields. */
export function buildCustomFieldColumnPickerOptions(
  readyFields: CustomFieldRecord[],
): ColumnPickerOption[] {
  return readyFields.map((field) => ({
    id: customFieldColumnId(field),
    label: customFieldDisplayName(field),
  }));
}

export interface PrunedCustomFieldColumnPrefs {
  /** The preference order with no-longer-Ready custom-field ids removed. */
  prunedOrder: string[];
  /** The dropped `custom.<name>` ids (a deleted field still stored as a column). */
  removedIds: string[];
}

/**
 * Drop `custom.<name>` ids that are absent from the Ready set from a stored
 * column-preference order, preserving `metadata.*` and static ids and the
 * original order. A stale custom-field id with no matching ColDef would
 * otherwise render as a blank ghost column (see `buildColumnsForPreferences`).
 */
export function pruneCustomFieldColumnPrefs(
  preferenceOrder: readonly string[],
  readyFields: CustomFieldRecord[],
): PrunedCustomFieldColumnPrefs {
  const readyColumnIds = new Set(readyFields.map(customFieldColumnId));
  const prunedOrder: string[] = [];
  const removedIds: string[] = [];
  for (const id of preferenceOrder) {
    if (isCustomFieldColumnId(id) && !readyColumnIds.has(id)) {
      removedIds.push(id);
    } else {
      prunedOrder.push(id);
    }
  }
  return { prunedOrder, removedIds };
}

export interface ComputeEffectiveColumnPrefsParams {
  /** Whether the release-custom-fields flag is on. */
  flagEnabled: boolean;
  /** Whether the Ready-fields query has settled (don't prune while loading). */
  readyFieldsFetched: boolean;
  preferenceOrder: readonly string[];
  readyFields: CustomFieldRecord[];
}

/**
 * Decide the effective column-preference order for a grid, given the flag and
 * Ready-query state. Three cases:
 *   - flag off: no custom-field columns are built, so strip any stale `custom.*`
 *     ids to avoid ghost stub columns — but report nothing (don't persist/alert
 *     a user out of the rollout group).
 *   - flag on, still loading: leave the order intact (a valid id may simply not
 *     have loaded its ColDef yet).
 *   - flag on, settled: prune deleted custom fields and report them.
 */
export function computeEffectiveColumnPrefs({
  flagEnabled,
  readyFieldsFetched,
  preferenceOrder,
  readyFields,
}: ComputeEffectiveColumnPrefsParams): PrunedCustomFieldColumnPrefs {
  if (!flagEnabled) {
    return {
      prunedOrder: preferenceOrder.filter((id) => !isCustomFieldColumnId(id)),
      removedIds: [],
    };
  }
  if (!readyFieldsFetched) {
    return { prunedOrder: [...preferenceOrder], removedIds: [] };
  }
  return pruneCustomFieldColumnPrefs(preferenceOrder, readyFields);
}

/** Strip the `custom.` prefix to recover the field name (e.g. for an alert). */
export function customFieldNameFromColumnId(id: string): string {
  return id.startsWith(CUSTOM_FIELD_ID_PREFIX)
    ? id.slice(CUSTOM_FIELD_ID_PREFIX.length)
    : id;
}
